import { Stack, Typography } from '@mui/material';
import PlanComparisionComponent from 'components/PlanComparisionScreen/PlanComparisionComponent';
import PlanPageLoader from 'components/PlanPageLoader/PlanPageLoader';
import { useLoadingStates } from 'hooks/useLoadingStates';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CreatePurchaseId } from 'services/api/paymentsApi';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchCurrentPlan } from 'store/reducers/currentPlanReducer';
import { updateFreeTrialEligibility } from 'store/reducers/freeTrialEligibilityReducers';
import { setSelectedSubscriptionInfo } from 'store/reducers/subscriptionReducer';
import { TPlanList, TPurchasePayload, TSubscriptionType } from 'typings';
import {
  APP_BUTTON_TYPES,
  isRunningInWebView,
  showAppButton
} from 'utils/webviewHelpers';

function UpgradeSubscription() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.subscription.upgradeSubscription'
  });

  const [currentSubscription, setCurrentSubscription] =
    useState<TSubscriptionType>();
  const [selectedCyclesPlans, setSelectedCyclesPlans] = useState<TPlanList[]>(
    []
  );
  const { id: subscriptionId } = useParams();
  const spaceData = useAppSelector((state) => state.space.spaceData);
  const { allPlans, isLoading: isAllPlansLoading } = useAppSelector(
    (state) => state.allPlans
  );
  const { subscriptionData, isLoading: isSubscriptionLoading } = useAppSelector(
    (state) => state.subscription
  );
  const { isLoading: isTrialEligibilityLoading } = useAppSelector(
    (state) => state.trial
  );
  const {
    currentPlanCycle,
    currentPlanLevel,
    isLoading: isCurrentPlanLoading
  } = useAppSelector((state) => state.currentPlan);

  const isLoading = useLoadingStates([
    isAllPlansLoading,
    isSubscriptionLoading,
    isTrialEligibilityLoading,
    isCurrentPlanLoading
  ]);

  const deviceId = location.state?.deviceId || null;

  const filterPlansForCycle = useCallback(
    (isYearlyCycle: boolean, planList: TPlanList[]) => {
      const cycleMonths = isYearlyCycle ? 12 : 1;
      const cyclePlans = planList.filter(
        (el: TPlanList) => el.subscription_cycle === cycleMonths
      );
      cyclePlans.sort(
        (a, b) => b.prices[0].original_price - a.prices[0].original_price
      );
      setSelectedCyclesPlans(cyclePlans);
    },
    []
  );

  useEffect(() => {
    if (allPlans?.length) {
      filterPlansForCycle(true, allPlans);
    }
  }, [allPlans, filterPlansForCycle]);

  useEffect(() => {
    if (spaceData?.id && !isSubscriptionLoading) {
      const existingSubscripiton = subscriptionData.find(
        (subscriptionDetail: TSubscriptionType) => {
          return subscriptionDetail.id === subscriptionId;
        }
      );
      setCurrentSubscription(existingSubscripiton);
      dispatch(
        updateFreeTrialEligibility({
          isFreeTrialEligible: existingSubscripiton?.trial
        })
      );
      if (existingSubscripiton?.sku_id) {
        dispatch(fetchCurrentPlan(existingSubscripiton?.sku_id));
      }
    }
  }, [spaceData, dispatch, subscriptionId, isSubscriptionLoading]);

  useEffect(() => {
    if (isRunningInWebView()) {
      showAppButton(APP_BUTTON_TYPES.RELOAD, APP_BUTTON_TYPES.CLOSE);
    }
  }, [isRunningInWebView]);

  const createPurchaseId = async (plan: TPlanList) => {
    try {
      const payload: TPurchasePayload = {
        country: plan?.prices[0]?.country,
        sku_id: plan.id,
        trial: currentSubscription?.trial || false,
        space_id: spaceData?.id,
        action: 'Upgrade',
        previous_purchase_id: currentSubscription?.id
      };

      const res = await CreatePurchaseId(payload);

      dispatch(
        setSelectedSubscriptionInfo({
          isUpgrade: true,
          isFreeTrial: currentSubscription?.trial || false,
          planSelected: plan,
          purchaseId: res.data?.purchase_id,
          deviceId,
          previousSubscriptionSKU: currentSubscription?.sku_id
        })
      );

      navigate('/reviewPurchase');
    } catch (err) {
      console.log('purchases err', err);
    }
  };

  const handlePlanSelection = (el: TPlanList) => {
    createPurchaseId(el);
  };

  const isSelectedPlanEligibleForUpgrade = (el: TPlanList) => {
    // If selected plan's subscription_cycle is greater then current plan's subscription cycle (upgrade subscription cycle wise is possible)
    if (el.subscription_cycle > currentPlanCycle) {
      // Return true if selected plan has level equal (means=> selected plan is yearly and current plan is Montly)or greater than current plan
      if (el.level >= currentPlanLevel) {
        return true;
      }
      // Return false if level of selected plan is less than current plan ( downgrade level wise is not possible)
      if (el.level < currentPlanLevel) {
        return false;
      }
    } // If selected plan's subscription_cycle is equal to current plan's subscription cycle ( upgrade to upper level is possible for same subscription cycle)
    else if (el.subscription_cycle === currentPlanCycle) {
      // selected plan's level is greater than current plan level -> can upgrade
      if (el.level > currentPlanLevel) {
        return true;
      }
      // selected plan's level is equal or less than current plan level -> can not upgrade
      return false;
    }
    // selected plan's subscription_cycle is less than current subscription_cycle -> can not upgrade
    return false;
  };

  if (isLoading) {
    return <PlanPageLoader canBuyPlan />;
  }

  if (!isLoading && selectedCyclesPlans?.length) {
    return (
      <Stack>
        {currentSubscription?.trial && (
          <Typography
            variant="label2"
            sx={{ textTransform: 'capitalize', textAlign: 'center' }}
          >
            {t('chooseFreeTrialPlan')}
          </Typography>
        )}
        {!currentSubscription?.trial && (
          <Typography
            variant="label2"
            sx={{ textTransform: 'capitalize', textAlign: 'center' }}
          >
            {t('upgradePlan')}
          </Typography>
        )}

        {!isLoading &&
          allPlans.length > 0 &&
          selectedCyclesPlans?.length > 0 && (
            <PlanComparisionComponent
              plansData={selectedCyclesPlans}
              allPlans={allPlans}
              allowSelection
              onPlanSelection={handlePlanSelection}
              isSelectedPlanEligibleForUpgrade={
                isSelectedPlanEligibleForUpgrade
              }
              isUpgrade
              onChange={filterPlansForCycle}
            />
          )}
      </Stack>
    );
  }
  return null;
}

export default UpgradeSubscription;
